import { useEffect, useState } from 'react'
import { useRiskDashboard } from '@/hooks/useRiskDashboard'
import { useAgents } from '@/hooks/useAgents'
import { usePortfolio } from '@/hooks/usePortfolio'
import DataSection from '@/components/ui/DataSection'
import PageHeader from '@/components/ui/PageHeader'
import {
  Shield, AlertTriangle, CheckCircle, XCircle, TrendingDown,
  Activity, BarChart3, Gauge, Lock, FileWarning, GitFork, Loader2,
  Globe, Layers, Grid3x3,
} from 'lucide-react'
import { api } from '@/lib/api'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  RadarChart, Radar, PolarGrid, PolarAngleAxis,
} from 'recharts'
import type {
  PotRuleStatus, StressScenario, PositionRisk, RiskCorrelationMatrix,
  RiskDecomposition, EffectiveBets, FxExposure, FactorExposureResult,
} from '@/lib/types'
import {
  getRiskCorrelation, getPortfolioRiskDecomposition, getFxExposure,
  getPortfolioFactorExposure,
} from '@/lib/api'

import { fmtGBP, fmtPct, fmtNum, fmtSigned } from '@/lib/format'
import { gridStroke, axisTickFill, tooltipContentStyle } from '@/lib/chartTheme'

// Sector-name aliases so a benchmark with "Healthcare" still matches a
// portfolio holding tagged "Health Care", etc. The portfolio side comes
// from compute_sector_breakdown (canonical GICS) but raw broker data
// sometimes uses regional spellings.
const SECTOR_ALIASES: Record<string, string[]> = {
  'Healthcare':            ['Health Care'],
  'Health Care':           ['Healthcare'],
  'Information Technology':['Technology'],
  'Technology':            ['Information Technology'],
  'Communication Services':['Communications'],
  'Communications':        ['Communication Services'],
}

function lookupBenchmark(sector: string, weights: Record<string, number>): number | undefined {
  if (sector in weights) return weights[sector]
  for (const alias of SECTOR_ALIASES[sector] ?? []) {
    if (alias in weights) return weights[alias]
  }
  return undefined
}

// ── Status colors ───────────────────────────────────────────────────────────

const STATUS_COLORS = {
  safe: { bg: 'bg-emerald/10', border: 'border-emerald/20', text: 'text-emerald', dot: '#10b981' },
  approaching: { bg: 'bg-amber/10', border: 'border-amber/20', text: 'text-amber', dot: '#f59e0b' },
  breached: { bg: 'bg-rose-light/10', border: 'border-rose-light/20', text: 'text-rose-light', dot: '#f43f5e' },
} as const

function severityColor(severity: string) {
  if (severity === 'high') return { bg: 'bg-rose-light/10', text: 'text-rose-light' }
  if (severity === 'medium') return { bg: 'bg-amber/10', text: 'text-amber' }
  return { bg: 'bg-emerald/10', text: 'text-emerald' }
}

// ── KPI Card ────────────────────────────────────────────────────────────────

function KPI({ label, value, sub, warn }: { label: string; value: string; sub?: string; warn?: boolean }) {
  return (
    <div className="stat-card">
      <p className="stat-label">{label}</p>
      <p className={`stat-value text-xl ${warn ? 'text-rose-light' : ''}`}>{value}</p>
      {sub && <p className={`text-xs ${warn ? 'text-rose-light' : 'text-text-muted'}`}>{sub}</p>}
    </div>
  )
}

// ── Effective Bets Card ─────────────────────────────────────────────────────

/**
 * Renders the Effective Number of Bets headline stat.
 * ENB = 1/Σrc_i² (inverse Herfindahl of post-covariance risk shares).
 * enb_pct < 40 → concentrated (rose); < 70 → moderate (amber); else diversified (emerald).
 */
function EffectiveBetsCard({ data }: { data: EffectiveBets }) {
  const { enb, enb_pct_of_holdings, concentration, note } = data

  if (enb == null || enb_pct_of_holdings == null) return null

  const colorMap = {
    concentrated: {
      border: 'border-rose-light/25',
      bg: 'bg-rose-light/5',
      badge: 'badge-red',
      text: 'text-rose-light',
      dot: 'bg-rose-light',
    },
    moderate: {
      border: 'border-amber/25',
      bg: 'bg-amber/5',
      badge: 'badge-amber',
      text: 'text-amber',
      dot: 'bg-amber',
    },
    diversified: {
      border: 'border-emerald/25',
      bg: 'bg-emerald/5',
      badge: 'badge-green',
      text: 'text-emerald',
      dot: 'bg-emerald',
    },
  } as const

  const colors = concentration ? colorMap[concentration] : colorMap.moderate
  const label = concentration
    ? concentration.charAt(0).toUpperCase() + concentration.slice(1)
    : 'Unknown'

  return (
    <div className={`rounded-lg border p-4 ${colors.border} ${colors.bg}`}>
      <div className="flex flex-wrap items-center gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-xs text-text-muted font-medium uppercase tracking-wide">
            Effective Number of Bets
          </p>
          <p className={`text-2xl font-bold font-mono mt-0.5 ${colors.text}`}>
            {fmtNum(enb, 1)}
            <span className="text-sm font-normal text-text-muted ml-2">
              / {fmtNum(enb_pct_of_holdings, 0)}% of nominal
            </span>
          </p>
        </div>
        <span className={`badge ${colors.badge} shrink-0`}>{label}</span>
      </div>
      {/* Progress bar: how much of N holdings are real bets */}
      <div className="h-1.5 bg-surface-hover rounded-full overflow-hidden mt-1 mb-2">
        <div
          className={`h-full rounded-full transition-all duration-500 ${colors.dot}`}
          style={{ width: `${Math.min(enb_pct_of_holdings, 100)}%` }}
        />
      </div>
      {note && (
        <p className="text-xs text-text-secondary leading-relaxed">{note}</p>
      )}
    </div>
  )
}

// ── Drawdown Gauge ──────────────────────────────────────────────────────────

function DrawdownGauge({ label, current, limit, className }: { label: string; current: number; limit: number; className?: string }) {
  const util = limit > 0 ? Math.min(current / limit * 100, 100) : 0
  const status = util > 100 ? 'breached' : util > 70 ? 'approaching' : 'safe'
  const colors = STATUS_COLORS[status]

  return (
    <div className={`p-4 rounded-lg border ${colors.bg} ${colors.border} ${className ?? ''}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-text-secondary">{label}</span>
        <span className={`text-xs font-bold ${colors.text}`}>{fmtPct(current)} / {fmtPct(limit)}</span>
      </div>
      <div className="h-2 bg-surface-hover rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.min(util, 100)}%`, backgroundColor: colors.dot }}
        />
      </div>
      <p className={`text-xs mt-1 ${colors.text}`}>{fmtPct(util)} utilised</p>
    </div>
  )
}

// ── Pot Rule Row ────────────────────────────────────────────────────────────

function PotRuleRow({ rule }: { rule: PotRuleStatus }) {
  const colors = STATUS_COLORS[rule.status]
  const Icon = rule.status === 'breached' ? XCircle : rule.status === 'approaching' ? AlertTriangle : CheckCircle

  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${colors.bg} ${colors.border}`}>
      <div className="flex items-center gap-2">
        <Icon size={14} className={colors.text} />
        <span className="text-sm text-text-secondary">{rule.label}</span>
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-text-muted">{rule.current} / {rule.limit}</span>
        <span className={`badge ${rule.status === 'breached' ? 'badge-red' : rule.status === 'approaching' ? 'badge-amber' : 'badge-green'}`}>
          {rule.status}
        </span>
      </div>
    </div>
  )
}

// ── Stress Test Row ─────────────────────────────────────────────────────────

function StressRow({ scenario, selected, onToggle }: {
  scenario: StressScenario
  selected: boolean
  onToggle: () => void
}) {
  const sc = severityColor(scenario.severity)
  return (
    <tr>
      <td className="py-3 pl-2 pr-1">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="accent-blue-400"
          aria-label={`Combine ${scenario.name}`}
        />
      </td>
      <td className="py-3 pr-3">
        <p className="text-sm font-medium text-text-primary">{scenario.name}</p>
        <p className="text-xs text-text-muted">{scenario.description}</p>
      </td>
      <td className="py-3 px-3 text-right">
        <span className="text-sm font-mono text-text-secondary">{scenario.shock_pct}%</span>
      </td>
      <td className="py-3 px-3 text-right">
        <span className={`text-sm font-mono font-semibold ${sc.text}`}>{fmtPct(scenario.impact_pct)}</span>
      </td>
      <td className="py-3 px-3 text-right">
        <span className={`text-sm font-mono font-semibold ${sc.text}`}>{fmtGBP(scenario.estimated_impact)}</span>
      </td>
      <td className="py-3 pl-3 text-right">
        <span className={`badge ${scenario.severity === 'high' ? 'badge-red' : scenario.severity === 'medium' ? 'badge-amber' : 'badge-green'}`}>
          {scenario.severity}
        </span>
      </td>
    </tr>
  )
}

// ── Correlation cell color helper ───────────────────────────────────────────

/** Maps a correlation value [-1, 1] to a background + text color. */
function corrCellStyle(val: number | null, isdiag: boolean): React.CSSProperties {
  if (isdiag) return { background: 'rgba(59,130,246,0.15)', color: 'rgb(var(--text-primary))' }
  if (val === null) return { background: 'transparent', color: 'rgb(var(--text-muted))' }
  // green at -1 → white at 0 → red at +1
  if (val > 0) {
    const alpha = Math.min(val, 1)
    return {
      background: `rgba(244, 63, 94, ${alpha * 0.7})`,
      color: alpha > 0.5 ? '#fff' : 'rgb(var(--text-primary))',
    }
  }
  const alpha = Math.min(Math.abs(val), 1)
  return {
    background: `rgba(16, 185, 129, ${alpha * 0.6})`,
    color: alpha > 0.5 ? '#fff' : 'rgb(var(--text-primary))',
  }
}

// ── Portfolio Correlation Matrix panel ──────────────────────────────────────

function CorrelationMatrixPanel({ data }: { data: RiskCorrelationMatrix }) {
  const { tickers, names, matrix, high_correlations } = data
  const n = tickers.length

  if (n === 0) {
    return (
      <p className="text-sm text-text-muted py-4">
        No holdings with yfinance symbols found — add symbols to your holdings to enable this view.
      </p>
    )
  }

  // Truncate ticker labels for display
  const label = (i: number) => tickers[i].length > 8 ? tickers[i].slice(0, 8) : tickers[i]

  return (
    <div className="space-y-5">
      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-text-muted">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-emerald/60" />
          Negative (good diversification)
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-surface-hover border border-border" />
          Uncorrelated
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-rose-light/70" />
          Positive (diversification risk)
        </span>
      </div>

      {/* Matrix grid */}
      <div className="overflow-x-auto">
        <table className="border-separate border-spacing-0.5 text-xs font-mono">
          <thead>
            <tr>
              <th scope="col" aria-label="Ticker" className="w-20" />
              {tickers.map((t, j) => (
                <th key={j} className="text-center text-text-muted font-medium pb-1 w-14">
                  {label(j)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.map((row, i) => (
              <tr key={i}>
                <td className="text-right text-text-muted font-medium pr-2 whitespace-nowrap">
                  {label(i)}
                </td>
                {row.map((val, j) => (
                  <td
                    key={j}
                    className="text-center rounded px-1 py-1.5 w-14"
                    style={corrCellStyle(val, i === j)} // eslint-disable-line react/forbid-component-props
                    title={`${tickers[i]} / ${tickers[j]}: ${val !== null ? val.toFixed(3) : 'n/a'}`}
                  >
                    {val !== null ? (i === j ? '1.00' : val.toFixed(2)) : '—'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Row labels: full names */}
      <div className="grid grid-cols-2 gap-1 text-xs text-text-muted">
        {tickers.map((t, i) => (
          <span key={i}><span className="font-mono text-text-secondary">{t}</span> — {names[i]}</span>
        ))}
      </div>

      {/* High-correlation callout */}
      {high_correlations.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-rose-light uppercase tracking-wide">
            High Correlation Pairs (&gt; 0.8) — Diversification Risk
          </p>
          <ul className="space-y-1.5">
            {high_correlations.map((pair, i) => (
              <li
                key={i}
                className="flex items-center justify-between p-2.5 rounded-lg border border-rose-light/20 bg-rose-light/5"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <AlertTriangle size={13} className="text-rose-light shrink-0" />
                  <span className="text-sm text-text-primary truncate">
                    <span className="font-mono">{pair.ticker1}</span>
                    <span className="text-text-muted mx-1">·</span>
                    <span className="font-mono">{pair.ticker2}</span>
                  </span>
                  <span className="text-xs text-text-muted hidden sm:block truncate">
                    {pair.name1} &amp; {pair.name2}
                  </span>
                </div>
                <span className="badge badge-red shrink-0 font-mono">{fmtNum(pair.correlation, 3)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald/20 bg-emerald/5">
          <CheckCircle size={14} className="text-emerald shrink-0" />
          <p className="text-sm text-text-secondary">
            No highly correlated pairs found — portfolio looks well-diversified across these holdings.
          </p>
        </div>
      )}
    </div>
  )
}

// ── Main Component ──────────────────────────────────────────────────────────

export default function RiskPage() {
  const { dashboard, stressTest, exposure, loading, fetchAll } = useRiskDashboard()
  const { analysis, runAnalysis, analyzing } = useAgents()
  const { fetchSnapshot } = usePortfolio()

  // Portfolio correlation — fetched independently with its own loading state
  const [corrData, setCorrData] = useState<RiskCorrelationMatrix | null>(null)
  const [corrLoading, setCorrLoading] = useState(false)
  const [corrError, setCorrError] = useState<string | null>(null)

  // Risk decomposition — covariance-based contribution per holding
  const [decomp, setDecomp] = useState<RiskDecomposition | null>(null)
  const [decompLoading, setDecompLoading] = useState(false)

  // FX exposure — currency split across discretionary holdings
  const [fx, setFx] = useState<FxExposure | null>(null)
  const [fxLoading, setFxLoading] = useState(false)

  // Sector breakdown with ETF look-through — for the heatmap vs benchmark
  const [sectorBd, setSectorBd] = useState<{
    total_gbp: number
    sectors: { sector: string; value_gbp: number; pct: number }[]
    coverage: { with_lookthrough: number; with_raw_sector: number; unclassified: number }
    benchmark_weights?: Record<string, number>
    benchmark_label?: string
  } | null>(null)
  const [sectorLoading, setSectorLoading] = useState(false)

  // Stress-test scenario combinator — set of scenario names ticked
  const [stressCombo, setStressCombo] = useState<Set<string>>(new Set())

  // Factor exposure (size / value / momentum / quality / market beta)
  const [factors, setFactors] = useState<FactorExposureResult | null>(null)
  const [factorsLoading, setFactorsLoading] = useState(false)

  useEffect(() => {
    fetchSnapshot()
    runAnalysis()
    fetchAll()
    // Kick off correlation fetch independently — can be slow (multiple yfinance calls)
    setCorrLoading(true)
    setCorrError(null)
    getRiskCorrelation()
      .then(d => setCorrData(d))
      .catch(() => setCorrError('Could not load correlation data. It will retry on next page visit.'))
      .finally(() => setCorrLoading(false))
    setDecompLoading(true)
    getPortfolioRiskDecomposition()
      .then(d => setDecomp(d))
      .catch(() => { /* keep page loading even if this fails */ })
      .finally(() => setDecompLoading(false))
    setFxLoading(true)
    getFxExposure()
      .then(d => setFx(d))
      .catch(() => { /* fall back to risk-page FX risk panel */ })
      .finally(() => setFxLoading(false))
    setSectorLoading(true)
    api.get('/api/sectors/portfolio-breakdown')
      .then(res => setSectorBd(res.data))
      .catch(() => { /* heatmap is optional */ })
      .finally(() => setSectorLoading(false))
    setFactorsLoading(true)
    getPortfolioFactorExposure()
      .then(d => setFactors(d))
      .catch(() => { /* factor tilts optional */ })
      .finally(() => setFactorsLoading(false))
  }, []) // eslint-disable-line

  const risk = analysis?.risk
  const d = dashboard

  // Radar data from agent analysis
  const radarData = risk ? [
    { metric: 'VaR',         value: Math.min(risk.var_95_daily / 200_000 * 100, 100) },
    { metric: 'Volatility',  value: Math.min(risk.volatility_annualized * 2.5, 100) },
    { metric: 'Beta',        value: Math.min(risk.beta / 2 * 100, 100) },
    { metric: 'Drawdown',    value: Math.min(risk.max_drawdown * 4, 100) },
    { metric: 'Concentration', value: 100 - (risk.is_compliant ? 20 : 70) },
  ] : []

  const scoreColor = !risk ? '#94a3b8'
    : risk.overall_risk_score < 3 ? '#10b981'
    : risk.overall_risk_score < 5 ? '#f59e0b'
    : risk.overall_risk_score < 7 ? '#f97316'
    : '#f43f5e'

  // Sector exposure chart data
  const sectorData = (exposure?.sectors ?? []).map(s => ({
    name: s.sector,
    value: s.weight_pct,
  })) ?? []

  const SECTOR_COLORS = ['#3b82f6', '#8b5cf6', '#10b981', '#f59e0b', '#f43f5e', '#06b6d4', '#e879f9']

  const isLoading = loading || analyzing
  const hasError = !isLoading && !d && !risk
  const errorMsg = hasError ? 'Risk data could not be loaded. Check your portfolio has holdings and the API is reachable.' : null

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        title="Risk Dashboard"
        icon={Shield}
        iconClassName="text-blue"
        subtitle={
          <>
            {risk ? `${risk.risk_label} risk · Score: ${fmtNum(risk.overall_risk_score, 1)}/10` : 'Loading risk data...'}
            {d ? ` · Portfolio: ${fmtGBP(d.portfolio_total)}` : ''}
          </>
        }
        actions={(d?.pot_rules ?? []).some(r => r.rule === 'paper_mode' && r.current === 'On') && (
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber/10 border border-amber/20">
            <FileWarning size={14} className="text-amber" />
            <span className="text-xs font-medium text-amber">PAPER MODE</span>
          </div>
        )}
      />

      <DataSection
        loading={isLoading}
        error={errorMsg}
        onRetry={() => { fetchAll(); runAnalysis() }}
        skeletonRows={8}
      >
      <div className="space-y-6">

      {/* ── Headline: overall risk score ─────────────────────────────────── */}
      {risk && (
        <div className="card p-5">
          <p className="panel-title">Overall risk score</p>
          <p className="stat-hero text-text-primary mt-1">
            {fmtNum(risk.overall_risk_score, 1)}
            <span className="text-xl text-text-muted font-normal"> / 10</span>
          </p>
          <p className="text-xs text-text-muted mt-1">
            {risk.risk_label} risk{d ? ` · Portfolio ${fmtGBP(d.portfolio_total)}` : ''}
          </p>
        </div>
      )}

      {/* ── KPI Row ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 stagger-children">
        <KPI
          label="VaR 95% (1-day)"
          value={d ? fmtGBP(d.var_1d_95) : '–'}
          sub="Max expected daily loss"
        />
        <KPI
          label="Max Drawdown"
          value={d ? fmtPct(d.from_peak_drawdown_pct) : '–'}
          sub="From peak"
          warn={d ? d.from_peak_drawdown_pct > 15 : false}
        />
        <KPI
          label="Sharpe Ratio"
          value={fmtNum(d?.sharpe_ratio)}
          sub={d ? (d.sharpe_ratio > 1 ? 'Good risk-adjusted return' : 'Below target') : undefined}
          warn={d ? d.sharpe_ratio < 1 : false}
        />
        <KPI
          label="Beta (vs S&P 500)"
          value={fmtNum(d?.beta)}
          sub={d ? (d.beta > 1 ? 'More volatile than market' : 'Less volatile') : undefined}
          warn={d ? d.beta > 1.3 : false}
        />
        <KPI
          label="Annualised Vol"
          value={d ? fmtPct(d.volatility_annualised) : '–'}
          sub="Portfolio volatility"
          warn={d ? d.volatility_annualised > 25 : false}
        />
      </div>

      {/* ── Extended Ratios ──────────────────────────────────────────────── */}
      {d && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPI label="VaR 99% (1-day)" value={fmtGBP(d.var_1d_99)} sub="Stress scenario" />
          <KPI label="VaR 95% (5-day)" value={fmtGBP(d.var_5d_95)} sub="Weekly risk" />
          <KPI label="Sortino Ratio" value={fmtNum(d.sortino_ratio)} sub="Downside-adjusted" />
          <KPI label="Calmar Ratio" value={fmtNum(d.calmar_ratio)} sub="Return / max drawdown" />
        </div>
      )}

      {/* ── Drawdown Gauges + Pot Rules ───────────────────────────────────── */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Drawdown Gauges */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Gauge size={16} className="text-blue" />
            <h2 className="panel-title">Drawdown Gauges</h2>
          </div>
          {d ? (
            <div className="space-y-3">
              <DrawdownGauge label="Daily Drawdown" current={d.daily_drawdown_pct} limit={5} />
              <DrawdownGauge label="Total Drawdown (from peak)" current={d.from_peak_drawdown_pct} limit={20} />
            </div>
          ) : (
            <p className="text-sm text-text-muted py-4">{isLoading ? 'Calculating...' : 'No data'}</p>
          )}
        </div>

        {/* Risk Dimensions Radar */}
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={16} className="text-blue" />
            <h2 className="panel-title">Risk Dimensions</h2>
          </div>
          {radarData.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={gridStroke} />
                <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: axisTickFill }} />
                <Radar dataKey="value" stroke={scoreColor} fill={scoreColor} fillOpacity={0.15} strokeWidth={2} />
                <Tooltip
                  formatter={(v: number) => [fmtPct(v, 0), 'Exposure']}
                  contentStyle={tooltipContentStyle}
                />
              </RadarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-52 flex items-center justify-center text-text-muted text-sm">
              {isLoading ? 'Calculating...' : 'Run analysis to see risk dimensions'}
            </div>
          )}
        </div>
      </div>

      {/* ── Pot Rules Status ──────────────────────────────────────────────── */}
      {d && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Lock size={16} className="text-blue" />
            <h2 className="panel-title">Pot Rules Status</h2>
            {d.pot_rules.some(r => r.rule === 'require_human_approval' && r.current === 'Yes') && (
              <span className="badge badge-blue ml-2">Approval Required</span>
            )}
          </div>
          <div className="space-y-2">
            {d.pot_rules.map(rule => (
              <PotRuleRow key={rule.rule} rule={rule} />
            ))}
          </div>
        </div>
      )}

      {/* ── Stress Test Panel ────────────────────────────────────────────── */}
      {stressTest && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown size={16} className="text-rose-light" />
            <h2 className="panel-title">Stress Test Scenarios</h2>
            <span className="text-xs text-text-muted ml-auto">Portfolio: {fmtGBP(stressTest.portfolio_total)}</span>
          </div>
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full">
              <thead>
                <tr>
                  <th className="!text-center px-2" title="Combine selected scenarios additively">Combo</th>
                  <th>Scenario</th>
                  <th className="!text-right px-3">Market Shock</th>
                  <th className="!text-right px-3">Portfolio Impact</th>
                  <th className="!text-right px-3">Est. Loss</th>
                  <th className="!text-right pl-3">Severity</th>
                </tr>
              </thead>
              <tbody>
                {stressTest.scenarios.map(sc => (
                  <StressRow
                    key={sc.name}
                    scenario={sc}
                    selected={stressCombo.has(sc.name)}
                    onToggle={() => {
                      const next = new Set(stressCombo)
                      if (next.has(sc.name)) next.delete(sc.name)
                      else next.add(sc.name)
                      setStressCombo(next)
                    }}
                  />
                ))}
                {stressCombo.size >= 2 && (() => {
                  const selected = stressTest.scenarios.filter(s => stressCombo.has(s.name))
                  const correlations = stressTest.correlations ?? {}
                  // Additive (upper bound, assumes ρ=1 between all)
                  const additivePct = selected.reduce((acc, s) => acc + s.impact_pct, 0)
                  // Correlation-aware: combined² = Σᵢ sᵢ² + 2·Σᵢ<ⱼ ρᵢⱼ·sᵢ·sⱼ
                  // Each sᵢ is the impact_pct (already negative). The result keeps
                  // the sign of the sum since shocks point the same way on aggregate.
                  let sumSq = 0
                  let crossSum = 0
                  for (let i = 0; i < selected.length; i++) {
                    sumSq += selected[i].impact_pct ** 2
                    for (let j = i + 1; j < selected.length; j++) {
                      const key = [selected[i].name, selected[j].name].sort().join('||')
                      const rho = correlations[key] ?? 0.5  // default mild correlation if unknown
                      crossSum += 2 * rho * selected[i].impact_pct * selected[j].impact_pct
                    }
                  }
                  const variance = sumSq + crossSum
                  // Quadratic-form magnitude, signed in the direction of the sum
                  const correlatedMag = variance > 0 ? Math.sqrt(variance) : 0
                  const correlatedPct = additivePct < 0 ? -correlatedMag : correlatedMag
                  const clampedPct = Math.max(correlatedPct, -100)
                  const correlatedLoss = stressTest.portfolio_total * (clampedPct / 100)
                  const additiveLoss = stressTest.portfolio_total * (Math.max(additivePct, -100) / 100)
                  const sev = Math.abs(clampedPct) >= 30 ? 'high' : Math.abs(clampedPct) >= 15 ? 'medium' : 'low'
                  const sevColor = severityColor(sev)
                  return (
                    <tr className="border-t-2 border-blue/30 bg-blue/5">
                      <td className="py-3 text-center">
                        <button
                          type="button"
                          onClick={() => setStressCombo(new Set())}
                          className="text-xs text-text-muted hover:text-text-primary"
                          title="Clear combo"
                        >
                          ✕
                        </button>
                      </td>
                      <td className="py-3 pr-3">
                        <p className="text-sm font-semibold text-blue">Combined ({stressCombo.size} scenarios)</p>
                        <p className="text-xs text-text-muted">
                          {selected.map(s => s.name).join(' + ')} · correlation-aware (quadratic form using pairwise ρ)
                        </p>
                        <p className="text-[11px] text-text-muted mt-0.5">
                          Naive additive upper bound: <span className="font-mono">{fmtPct(Math.max(additivePct, -100))}</span> ({fmtGBP(additiveLoss)})
                        </p>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className="text-sm font-mono text-text-muted">—</span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className={`text-sm font-mono font-bold ${sevColor.text}`}>
                          {correlatedPct < -100 ? '<' : ''}{fmtPct(clampedPct)}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <span className={`text-sm font-mono font-bold ${sevColor.text}`}>{fmtGBP(correlatedLoss)}</span>
                      </td>
                      <td className="py-3 pl-3 text-right">
                        <span className={`badge ${sev === 'high' ? 'badge-red' : sev === 'medium' ? 'badge-amber' : 'badge-green'}`}>
                          {sev}
                        </span>
                      </td>
                    </tr>
                  )
                })()}
              </tbody>
            </table>
          </div>
          {stressCombo.size < 2 && (
            <p className="text-xs text-text-muted mt-3">
              Tip: tick 2+ scenarios to model a compound shock (e.g. 2008 + Rate Shock).
            </p>
          )}
        </div>
      )}

      {/* ── Exposure Analysis ─────────────────────────────────────────────── */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Sector Exposure Chart */}
        {sectorData.length > 0 && (
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={16} className="text-blue" />
              <h2 className="panel-title">Sector Exposure</h2>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={sectorData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11, fill: axisTickFill }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: axisTickFill }} width={100} />
                <Tooltip
                  formatter={(v: number) => [fmtPct(v, 1), 'Weight']}
                  contentStyle={tooltipContentStyle}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {sectorData.map((_, i) => (
                    <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Geographic Exposure */}
        {exposure && exposure.regions.length > 0 && (
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 size={16} className="text-blue" />
              <h2 className="panel-title">Geographic Exposure</h2>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart
                data={exposure.regions.map(r => ({ name: r.sector, value: r.weight_pct }))}
                layout="vertical"
                margin={{ left: 10, right: 20 }}
              >
                <XAxis type="number" tickFormatter={(v: number) => `${v}%`} tick={{ fontSize: 11, fill: axisTickFill }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: axisTickFill }} width={110} />
                <Tooltip
                  formatter={(v: number) => [fmtPct(v, 1), 'Weight']}
                  contentStyle={tooltipContentStyle}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} fill="#8b5cf6" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── Concentration Warnings ────────────────────────────────────────── */}
      {exposure && exposure.concentration_warnings.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-amber" />
            <h2 className="panel-title">Concentration Warnings</h2>
            <span className="badge badge-amber ml-1">{exposure.concentration_warnings.length}</span>
          </div>
          <ul className="space-y-2.5">
            {exposure.concentration_warnings.map((w, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-amber/5 border border-amber/20 rounded-lg">
                <AlertTriangle size={14} className="text-amber shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">{w.name} ({w.symbol})</p>
                  <p className="text-xs text-text-secondary mt-0.5">{w.message}</p>
                </div>
                <span className="badge badge-amber shrink-0">{fmtPct(w.weight_pct)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Position-Level Risk ────────────────────────────────────────────── */}
      {d && d.positions.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Activity size={16} className="text-blue" />
            <h2 className="panel-title">Position-Level Risk</h2>
          </div>
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full">
              <thead>
                <tr>
                  <th>Position</th>
                  <th className="!text-right px-3">Value</th>
                  <th className="!text-right px-3">Weight</th>
                  <th className="!text-right px-3">Volatility</th>
                  <th className="!text-right pl-3">VaR Contribution</th>
                </tr>
              </thead>
              <tbody>
                {d.positions
                  .sort((a: PositionRisk, b: PositionRisk) => b.var_contribution - a.var_contribution)
                  .map((pos: PositionRisk) => (
                  <tr key={pos.holding_id}>
                    <td className="py-3 pr-3">
                      <p className="text-sm font-medium text-text-primary">{pos.name}</p>
                      <p className="text-xs text-text-muted">{pos.symbol} · {pos.sector}</p>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span className="text-sm font-mono text-text-secondary">{fmtGBP(pos.value)}</span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span className={`text-sm font-mono ${pos.weight_pct > 15 ? 'text-rose-light font-semibold' : 'text-text-secondary'}`}>
                        {fmtPct(pos.weight_pct)}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-right">
                      <span className={`text-sm font-mono ${pos.volatility > 25 ? 'text-amber' : 'text-text-secondary'}`}>
                        {fmtPct(pos.volatility)}
                      </span>
                    </td>
                    <td className="py-3 pl-3 text-right">
                      <span className="text-sm font-mono font-semibold text-text-primary">{fmtGBP(pos.var_contribution)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Agent Position Warnings (from original) ───────────────────────── */}
      {risk && risk.position_warnings.length > 0 && (
        <div className="card p-5">
          <div className="flex items-center gap-2 mb-4">
            <AlertTriangle size={16} className="text-amber" />
            <h2 className="panel-title">Agent Position Warnings</h2>
            <span className="badge badge-amber ml-1">{risk.position_warnings.length}</span>
          </div>
          <ul className="space-y-2.5">
            {risk.position_warnings.map(w => (
              <li key={w.holding_id} className="flex items-start gap-3 p-3 bg-amber/5 border border-amber/20 rounded-lg">
                <AlertTriangle size={14} className="text-amber shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-medium text-text-primary">{w.name}</p>
                  <p className="text-xs text-text-secondary mt-0.5">{w.message}</p>
                </div>
                <span className={`badge shrink-0 ${w.severity === 'critical' ? 'badge-red' : 'badge-amber'}`}>
                  {w.severity}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── FX Exposure ─────────────────────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Globe size={16} className="text-blue" />
          <h2 className="panel-title">FX Exposure</h2>
          {fx?.gbpusd_rate ? (
            <span className="text-xs text-text-muted ml-auto">GBP/USD {fmtNum(fx.gbpusd_rate, 4)}</span>
          ) : null}
        </div>
        {fxLoading ? (
          <div className="flex items-center gap-2 py-4 text-text-muted text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span>Computing currency exposure…</span>
          </div>
        ) : fx && fx.exposures.length > 0 ? (
          <div className="space-y-2">
            {fx.exposures.map(ex => {
              const isGain = ex.pnl_gbp >= 0
              return (
                <div key={ex.currency} className="flex items-center gap-3 p-3 bg-surface-hover rounded-lg">
                  <span className="text-sm font-mono font-semibold text-text-primary w-12">{ex.currency}</span>
                  <div className="flex-1 min-w-0">
                    <div className="h-2 bg-surface rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue rounded-full transition-all"
                        style={{ width: `${Math.min(ex.weight_pct, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="text-sm font-mono text-text-secondary w-16 text-right">{fmtPct(ex.weight_pct)}</span>
                  <span className="text-sm font-mono text-text-secondary w-24 text-right">{fmtGBP(ex.value_gbp)}</span>
                  <span className={`text-xs font-mono w-20 text-right ${isGain ? 'text-emerald' : 'text-rose-light'}`}>
                    {isGain ? '+' : ''}{fmtGBP(ex.pnl_gbp)}
                  </span>
                </div>
              )
            })}
            <p className="text-xs text-text-muted pt-2">
              Total exposure {fmtGBP(fx.total_value_gbp)} · Each 1% GBP move on non-GBP {fmtPct(100 - (fx.exposures.find(e => e.currency === 'GBP')?.weight_pct ?? 0))} ≈ {fmtGBP(fx.total_value_gbp * (100 - (fx.exposures.find(e => e.currency === 'GBP')?.weight_pct ?? 0)) / 10000)} swing
            </p>
          </div>
        ) : (
          <p className="text-sm text-text-muted">No FX data yet — try after the next price refresh.</p>
        )}
      </div>

      {/* ── Risk Decomposition (covariance) ─────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Layers size={16} className="text-purple" />
          <h2 className="panel-title">Risk Decomposition</h2>
          {decomp ? (
            <span className="text-xs text-text-muted ml-auto" title="Ledoit-Wolf shrinkage of covariance toward constant-correlation target. δ=0 = pure sample, δ=1 = pure target. Coverage = holdings with ≥30 days of price history.">
              σ {fmtPct(decomp.portfolio_volatility)} · 95% VaR {fmtGBP(decomp.portfolio_var_95)}
              {decomp.shrinkage_intensity !== undefined && (
                <> · LW δ {fmtNum(decomp.shrinkage_intensity)}</>
              )}
              {decomp.covered_assets !== undefined && decomp.total_assets !== undefined && (
                <> · {decomp.covered_assets}/{decomp.total_assets} covered</>
              )}
            </span>
          ) : null}
        </div>
        {decompLoading ? (
          <div className="flex items-center gap-2 py-4 text-text-muted text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span>Computing covariance contributions…</span>
          </div>
        ) : decomp && decomp.contributions.length > 0 ? (
          <div className="space-y-3">
            {/* Effective Bets headline — null-safe, only shown when data present */}
            {decomp.effective_bets?.enb != null && (
              <EffectiveBetsCard data={decomp.effective_bets} />
            )}
            <div className="space-y-1.5">
            <div className="flex items-center gap-3 px-3 pb-1 text-xs text-text-muted border-b border-border">
              <span className="w-32">Holding</span>
              <span className="flex-1">Risk Contribution</span>
              <span className="w-16 text-right">Weight</span>
              <span className="w-20 text-right">Risk %</span>
            </div>
            {decomp.contributions.slice(0, 12).map(c => {
              const overweighted = c.risk_contribution_pct > c.weight_pct * 1.3
              return (
                <div key={c.symbol} className="flex items-center gap-3 px-3 py-2 hover:bg-surface-hover rounded">
                  <div className="w-32 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{c.symbol}</p>
                    <p className="text-xs text-text-muted truncate">{c.name}</p>
                  </div>
                  <div className="flex-1">
                    <div className="h-2 bg-surface-hover rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${overweighted ? 'bg-amber' : 'bg-purple'}`}
                        style={{ width: `${Math.min(c.risk_contribution_pct, 100)}%` }}
                      />
                    </div>
                  </div>
                  <span className="w-16 text-right text-xs font-mono text-text-muted">{fmtPct(c.weight_pct)}</span>
                  <span className={`w-20 text-right text-sm font-mono ${overweighted ? 'text-amber font-semibold' : 'text-text-secondary'}`}>
                    {fmtPct(c.risk_contribution_pct)}
                  </span>
                </div>
              )
            })}
            <p className="text-xs text-text-muted pt-2">
              Bars in <span className="text-amber">amber</span> contribute &gt;30% more risk than weight implies — candidates for trimming.
            </p>
            </div>
          </div>
        ) : (
          <p className="text-sm text-text-muted">Not enough price history to decompose risk yet.</p>
        )}
      </div>

      {/* ── Sector Concentration Heatmap ──────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <Grid3x3 size={16} className="text-amber" />
          <h2 className="panel-title">Sector Concentration</h2>
          <span className="text-xs text-text-muted ml-auto">
            ETF look-through · tilt vs {sectorBd?.benchmark_label ?? 'S&P 500'}
          </span>
        </div>
        {sectorLoading ? (
          <div className="flex items-center gap-2 py-4 text-text-muted text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span>Decomposing ETFs into GICS sectors…</span>
          </div>
        ) : sectorBd && sectorBd.sectors.length > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {sectorBd.sectors.map(s => {
                const ref = lookupBenchmark(s.sector, sectorBd.benchmark_weights ?? {})
                const tilt = ref !== undefined ? s.pct - ref : null
                let tone = 'border-border bg-surface-hover'
                let tiltText = 'text-text-muted'
                if (tilt !== null) {
                  const abs = Math.abs(tilt)
                  if (abs >= 10) {
                    tone = 'border-rose-light/30 bg-rose-light/5'
                    tiltText = 'text-rose-light'
                  } else if (abs >= 5) {
                    tone = 'border-amber/30 bg-amber/5'
                    tiltText = 'text-amber'
                  } else {
                    tone = 'border-emerald/30 bg-emerald/5'
                    tiltText = 'text-emerald'
                  }
                }
                return (
                  <div key={s.sector} className={`rounded-lg border p-3 ${tone}`}>
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <p className="text-xs font-medium text-text-primary truncate" title={s.sector}>
                        {s.sector}
                      </p>
                      {tilt !== null && (
                        <span className={`text-[10px] font-mono font-bold ${tiltText}`}>
                          {tilt >= 0 ? '+' : ''}{tilt.toFixed(1)}pp
                        </span>
                      )}
                    </div>
                    <p className="text-lg font-bold font-mono text-text-primary">{fmtPct(s.pct)}</p>
                    <p className="text-[10px] text-text-muted font-mono mt-0.5">
                      {fmtGBP(s.value_gbp)}{ref !== undefined && <> · bench {ref.toFixed(1)}%</>}
                    </p>
                  </div>
                )
              })}
            </div>
            <div className="flex items-center gap-3 text-[11px] text-text-muted pt-3 mt-3 border-t border-border flex-wrap">
              <span>
                Coverage: <span className="text-text-secondary">{sectorBd.coverage.with_lookthrough}</span> ETF look-through ·{' '}
                <span className="text-text-secondary">{sectorBd.coverage.with_raw_sector}</span> direct ·{' '}
                <span className="text-text-secondary">{sectorBd.coverage.unclassified}</span> unclassified
              </span>
              <span className="ml-auto">
                <span className="inline-block w-2 h-2 rounded-sm bg-emerald mr-1" /> ≤5pp
                <span className="inline-block w-2 h-2 rounded-sm bg-amber ml-2 mr-1" /> 5-10pp
                <span className="inline-block w-2 h-2 rounded-sm bg-rose-light ml-2 mr-1" /> &gt;10pp
              </span>
            </div>
          </>
        ) : (
          <p className="text-sm text-text-muted">No sector data yet — try a reprice first.</p>
        )}
      </div>

      {/* ── Factor Tilts ──────────────────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 size={16} className="text-purple" />
          <h2 className="panel-title">Factor Tilts</h2>
          {factors && (
            <span className="text-xs text-text-muted ml-auto">
              β {fmtNum(factors.market_beta)} · {factors.size_tilt} / {factors.style_tilt}
            </span>
          )}
        </div>
        {factorsLoading ? (
          <div className="flex items-center gap-2 py-4 text-text-muted text-sm">
            <Loader2 size={14} className="animate-spin" />
            <span>Computing Fama-French style factor exposure…</span>
          </div>
        ) : factors ? (
          <div className="space-y-2.5">
            {factors.factors.map(f => {
              const exp = f.exposure
              // Normalise -1..+1 (or -2..+2 for beta) to bar position
              const isBeta = f.name === 'Market'
              const max = isBeta ? 2 : 1
              const clamped = Math.max(-max, Math.min(max, exp))
              const positive = clamped >= 0
              const widthPct = Math.abs(clamped / max) * 50  // each side is 50% of bar
              return (
                <div key={f.name} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-3 min-w-0">
                    <p className="text-sm font-medium text-text-primary truncate">{f.name}</p>
                    <p className="text-[11px] text-text-muted truncate">{f.description}</p>
                  </div>
                  <div className="col-span-7 relative h-3 bg-surface-hover rounded-full overflow-hidden">
                    <div className="absolute inset-y-0 left-1/2 w-px bg-border" />
                    <div
                      className={`absolute inset-y-0 ${positive ? 'left-1/2 bg-emerald' : 'right-1/2 bg-rose-light'}`}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  <div className="col-span-2 text-right">
                    <span className={`text-sm font-mono font-bold ${positive ? 'text-emerald' : 'text-rose-light'}`}>
                      {fmtSigned(exp)}
                    </span>
                  </div>
                </div>
              )
            })}
            <p className="text-[11px] text-text-muted pt-2 mt-2 border-t border-border">
              Estimated from sector exposure + risk levels — directional read, not a regression.
              Positive Size = small-cap tilt; positive Value = value vs growth tilt; positive Momentum = trending up.
            </p>
          </div>
        ) : (
          <p className="text-sm text-text-muted">Not enough portfolio data to compute factor tilts.</p>
        )}
      </div>

      {/* ── Portfolio Correlation Matrix ───────────────────────────────────── */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-4">
          <GitFork size={16} className="text-blue" />
          <h2 className="panel-title">Portfolio Correlation Matrix</h2>
          <span className="text-xs text-text-muted ml-auto">Top 10 holdings · 1 year daily returns</span>
        </div>
        {corrLoading ? (
          <div className="flex items-center gap-2 py-6 text-text-muted text-sm">
            <Loader2 size={16} className="animate-spin" />
            <span>Fetching price history for your holdings… this takes a few seconds.</span>
          </div>
        ) : corrError ? (
          <div className="flex items-center gap-2 p-3 rounded-lg border border-amber/20 bg-amber/5">
            <AlertTriangle size={14} className="text-amber shrink-0" />
            <p className="text-sm text-text-secondary">{corrError}</p>
          </div>
        ) : corrData ? (
          <CorrelationMatrixPanel data={corrData} />
        ) : null}
      </div>

      {/* ── All Clear Banner ──────────────────────────────────────────────── */}
      {risk && risk.position_warnings.length === 0 &&
       risk.sector_concentration_warnings.length === 0 &&
       (!exposure || exposure.concentration_warnings.length === 0) && (
        <div className="card p-5 flex items-center gap-3 border-emerald/20 bg-emerald/5">
          <CheckCircle size={20} className="text-emerald shrink-0" />
          <div>
            <p className="font-semibold text-text-primary text-sm">All clear</p>
            <p className="text-xs text-text-secondary">No position limit or concentration breaches detected.</p>
          </div>
        </div>
      )}

      </div>{/* end inner space-y-6 */}
      </DataSection>
    </div>
  )
}
